import Space from '@/components/Space';
import OneDriveIcon from '../utils/icons/OneDriveIcon';
import {
  ConnectedAccountBox,
  ConnectedAccountInfo,
  ConnectedAccountWrapper,
} from './styled-components';
import UserAvatar from '@/components/UserAvatar';
import { useEffect, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { type UserSummary } from '@/lib/types';
import { microsoftLoginRequest } from '../utils/microsoft/useGraphClient/useGetMicrosoftAccessToken';
import AccountInfoLoadingMessage from './utils/AccountInfoLoadingMessage';
import NoAccountConnectedMessage from './utils/NoAccountConnectedMessage';
import DisconnectAccountButton from './utils/DisconnectAccountButton';
import { FormattedMessage, useIntl } from 'react-intl';

const MicrosoftAccountSettings = () => {
  const { instance: msalInstance } = useMsal();
  const intl = useIntl();

  const [loading, setLoading] = useState(true);
  const [accountInfo, setAccountInfo] = useState<UserSummary | null>(null);

  useEffect(() => {
    const getAccountInfo = async (): Promise<void> => {
      try {
        const tokenResponse = await msalInstance.acquireTokenSilent(
          microsoftLoginRequest,
        );

        setAccountInfo({
          name:
            tokenResponse.account.name ??
            intl.formatMessage({
              id: 'portfolio.challenge.files.add.connected_accounts_dialog.microsoft_account_settings.account_name_placeholder',
            }),
          email: tokenResponse.account.username,
          picture: null,
        });
      } catch {
        // no-op: no account connected
      } finally {
        setLoading(false);
      }
    };

    getAccountInfo();
  }, [msalInstance, intl]);

  const [isDisconnecting, setIsDisconnecting] = useState(false);

  const disconnectAccount = async (): Promise<void> => {
    setIsDisconnecting(true);

    await msalInstance.clearCache();

    setAccountInfo(null);
    setIsDisconnecting(false);
  };

  return (
    <ConnectedAccountWrapper>
      <h3>
        <OneDriveIcon />
        <Space width={8} />
        <FormattedMessage id="portfolio.challenge.files.add.connected_accounts_dialog.microsoft_account_settings.title" />
      </h3>
      <ConnectedAccountBox>
        {loading ? (
          <AccountInfoLoadingMessage />
        ) : accountInfo === null ? (
          <NoAccountConnectedMessage variant="microsoft" />
        ) : (
          <>
            <div>
              <UserAvatar
                user={{
                  ...accountInfo,
                  name: accountInfo.name.startsWith('(')
                    ? ''
                    : accountInfo.name,
                }}
                size={36}
                marginRight={12}
              />
              <ConnectedAccountInfo>
                <h4>{accountInfo.name}</h4>
                <p>{accountInfo.email}</p>
              </ConnectedAccountInfo>
            </div>
            <DisconnectAccountButton
              onClick={disconnectAccount}
              loading={isDisconnecting}
            />
          </>
        )}
      </ConnectedAccountBox>
    </ConnectedAccountWrapper>
  );
};

export default MicrosoftAccountSettings;
